import { offeredRungs, offeredOptions } from './chunk-INX63VAT.js';
import { TENANT_PATH_ANY } from './chunk-WSK4D2NS.js';

// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var GLOBAL_TENANT_COMPOSITE = "main-main";

// src/tenancy/record-scopes.ts
var TX_SCOPES = [
  {
    id: "user",
    label: "My view",
    description: "Your own private runs, plus everything shared across your workspace.",
    kind: "persisted"
  },
  {
    id: "tenant",
    workspaceLabel: "Workspace",
    workspaceDescription: "Everything shared across your workspace.",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Tenant",
    description: "Everything shared across your current tenant.",
    kind: "persisted"
  },
  {
    id: "org",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Organization",
    description: "The organization-level feed, shared across this org's tenants.",
    kind: "persisted"
  },
  {
    id: "customer",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Customer",
    description: "The customer-level feed, shared across the whole account.",
    kind: "persisted"
  },
  {
    id: "global",
    soloLabel: "All transactions",
    soloDescription: "The shared record of on-chain transactions.",
    label: "Global",
    description: "The shared global record of on-chain transactions, system-wide.",
    kind: "persisted"
  },
  {
    id: "latest",
    label: "Latest blocks",
    description: "Live transactions from the latest block on each preview chain.",
    kind: "live"
  }
];
function txScopeNaming(def, offered) {
  if (def.kind !== "persisted") return { label: def.label, description: def.description };
  const rungs = offered.filter((s) => s.kind === "persisted");
  if (rungs.length <= 1) {
    return {
      label: def.soloLabel ?? def.label,
      description: def.soloDescription ?? def.description
    };
  }
  const wider = rungs.some((s) => s.id === "org" || s.id === "customer" || s.id === "global");
  if (wider) return { label: def.label, description: def.description };
  return {
    label: def.workspaceLabel ?? def.label,
    description: def.workspaceDescription ?? def.description
  };
}
var DEFAULT_TX_SCOPE_ID = "user";
var TX_TENANCY_SCOPES = TX_SCOPES.filter(
  (s) => s.kind === "persisted"
);
var TRANSACTION_SCOPES_KEY = "transaction_scopes";
function scopesFrom(metadata) {
  const raw = metadata?.[TRANSACTION_SCOPES_KEY];
  if (!Array.isArray(raw)) return null;
  const known = raw.filter((v) => typeof v === "string" && TX_SCOPES.some((s) => s.id === v));
  return known.length > 0 ? new Set(known) : null;
}
function offeredTxScopes(planMetadata, subscriptionMetadata = null) {
  const rungs = offeredRungs(planMetadata, subscriptionMetadata);
  const onLadder = offeredOptions(TX_SCOPES, rungs, (s) => s.id);
  const allowed = scopesFrom(subscriptionMetadata) ?? scopesFrom(planMetadata);
  if (!allowed) return onLadder;
  const offered = onLadder.filter((s) => allowed.has(s.id));
  return offered.length > 0 ? offered : onLadder;
}
function offeredTxTenancyScopes(planMetadata, subscriptionMetadata = null) {
  const offered = offeredTxScopes(planMetadata, subscriptionMetadata).filter((s) => s.kind === "persisted");
  return offered.length > 0 ? offered : TX_TENANCY_SCOPES;
}
function offeredTxScopeOr(wanted, offered, ...fallbacks) {
  for (const id of [wanted, ...fallbacks]) if (offered.some((s) => s.id === id)) return id;
  return offered[0]?.id ?? wanted;
}
function isTxTenancyScopeId(value) {
  return TX_TENANCY_SCOPES.some((s) => s.id === value);
}
function txScope(id) {
  return TX_SCOPES.find((s) => s.id === id) ?? TX_SCOPES.find((s) => s.id === DEFAULT_TX_SCOPE_ID);
}
function txScopeOverrides(scope) {
  switch (scope) {
    case "tenant":
      return { user_id: TENANT_PATH_ANY };
    case "org":
      return { tenant_id: GLOBAL_TENANT };
    case "customer":
      return { org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "global":
      return { customer_id: GLOBAL_TENANT, org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "user":
    case "latest":
    default:
      return {};
  }
}
function txScopeRequest(scope, callerUserId) {
  if (scope === "latest") return { params: {}, degradedTo: null, reason: null };
  if (scope === "user") {
    if (!callerUserId) {
      return {
        params: { scope: "tenant", ...txScopeOverrides("tenant") },
        degradedTo: "tenant",
        reason: "Your own feed needs your user id, and this session has not resolved one \u2014 showing the tenant feed instead."
      };
    }
    return {
      params: { scope: "user", user_id: callerUserId },
      degradedTo: null,
      reason: null
    };
  }
  return { params: { scope, ...txScopeOverrides(scope) }, degradedTo: null, reason: null };
}
function scopeQueryParams(scope, callerUserId) {
  return txScopeRequest(scope, callerUserId).params;
}

export { DEFAULT_TX_SCOPE_ID, GLOBAL_TENANT, GLOBAL_TENANT_COMPOSITE, TRANSACTION_SCOPES_KEY, TX_SCOPES, TX_TENANCY_SCOPES, isTxTenancyScopeId, offeredTxScopeOr, offeredTxScopes, offeredTxTenancyScopes, scopeQueryParams, txScope, txScopeNaming, txScopeOverrides, txScopeRequest };
//# sourceMappingURL=chunk-NIAN6HZO.js.map
//# sourceMappingURL=chunk-NIAN6HZO.js.map